import type { SignatureBiometrics, SignatureGeometry } from "@ssf/sign-worker";
import type React from "react";
import { useRef } from "react";

type Point = { x: number; y: number; t: number };
type StrokeRecord = { startMs: number; endMs: number; points: Point[] };

const mean = (values: number[]) =>
  values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;

function computeGeometry(
  strokes: StrokeRecord[],
  canvasWidth: number,
  canvasHeight: number,
  totalDrawingDurationMs: number,
): SignatureGeometry | undefined {
  const allPoints = strokes.flatMap((s) => s.points);
  if (allPoints.length < 2) {
    return undefined;
  }

  let pathLengthPx = 0;
  const segmentVelocities: number[] = [];
  for (const stroke of strokes) {
    for (let i = 1; i < stroke.points.length; i++) {
      const prev = stroke.points[i - 1];
      const curr = stroke.points[i];
      const dist = Math.hypot(curr.x - prev.x, curr.y - prev.y);
      pathLengthPx += dist;
      const dtMs = curr.t - prev.t;
      if (dtMs > 0) {
        segmentVelocities.push((dist / dtMs) * 1000);
      }
    }
  }

  const durationS = totalDrawingDurationMs / 1000;
  const meanVelocityPxPerS = durationS > 0 ? pathLengthPx / durationS : 0;
  const peakVelocityPxPerS = segmentVelocities.length
    ? Math.max(...segmentVelocities)
    : 0;
  const velMean = mean(segmentVelocities);
  const velocityCv =
    velMean > 0
      ? Math.sqrt(mean(segmentVelocities.map((v) => (v - velMean) ** 2))) /
        velMean
      : 0;

  const xs = allPoints.map((p) => p.x);
  const ys = allPoints.map((p) => p.y);
  const widthPx = Math.max(...xs) - Math.min(...xs);
  const heightPx = Math.max(...ys) - Math.min(...ys);

  return {
    pathLengthPx,
    meanVelocityPxPerS,
    peakVelocityPxPerS,
    velocityCv,
    boundingBox: {
      widthPx,
      heightPx,
      aspectRatio: heightPx > 0 ? widthPx / heightPx : 0,
    },
    centroid: {
      xFrac: canvasWidth > 0 ? mean(xs) / canvasWidth : 0,
      yFrac: canvasHeight > 0 ? mean(ys) / canvasHeight : 0,
    },
  };
}

export function useSignatureBiometrics(
  canvasWidth: number,
  canvasHeight: number,
) {
  const mountTimeRef = useRef(Date.now());
  const strokesRef = useRef<StrokeRecord[]>([]);
  const activeStrokeStartRef = useRef<number | null>(null);
  const isDrawingRef = useRef(false);
  const currentPointsRef = useRef<Point[]>([]);
  const containerRectRef = useRef<DOMRect | null>(null);
  const pointerTypeRef = useRef<string | undefined>(undefined);

  const finalizeStroke = () => {
    if (!isDrawingRef.current || activeStrokeStartRef.current === null) {
      return;
    }
    isDrawingRef.current = false;
    const endMs = Date.now();
    strokesRef.current = [
      ...strokesRef.current,
      {
        startMs: activeStrokeStartRef.current,
        endMs,
        points: currentPointsRef.current,
      },
    ];
    activeStrokeStartRef.current = null;
  };

  const computeBiometrics = (): SignatureBiometrics => {
    const inProgress: StrokeRecord[] =
      isDrawingRef.current && activeStrokeStartRef.current !== null
        ? [
            {
              startMs: activeStrokeStartRef.current,
              endMs: Date.now(),
              points: currentPointsRef.current,
            },
          ]
        : [];
    const strokes = [...strokesRef.current, ...inProgress];

    const firstStrokeDelayMs =
      strokes.length > 0 ? strokes[0].startMs - mountTimeRef.current : 0;

    const totalDrawingDurationMs = strokes.reduce(
      (sum, s) => sum + (s.endMs - s.startMs),
      0,
    );

    return {
      firstStrokeDelayMs,
      totalDrawingDurationMs,
      strokeCount: strokes.length,
      strokeTimings: strokes.map((s) => ({
        durationMs: s.endMs - s.startMs,
        pointCount: s.points.length,
      })),
      interStrokePauseMs: strokes
        .slice(1)
        .map((s, i) => s.startMs - strokes[i].endMs),
      canvasWidth,
      canvasHeight,
      pointerType: pointerTypeRef.current,
      geometry: computeGeometry(
        strokes,
        canvasWidth,
        canvasHeight,
        totalDrawingDurationMs,
      ),
    };
  };

  const pointerHandlers = {
    onPointerDown: (e: React.PointerEvent) => {
      if (pointerTypeRef.current === undefined) {
        pointerTypeRef.current = e.pointerType;
      }
      containerRectRef.current = e.currentTarget.getBoundingClientRect();
      activeStrokeStartRef.current = Date.now();
      isDrawingRef.current = true;
      currentPointsRef.current = [];
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (!isDrawingRef.current) {
        return;
      }
      const rect = containerRectRef.current;
      currentPointsRef.current.push({
        x: rect ? e.clientX - rect.left : e.clientX,
        y: rect ? e.clientY - rect.top : e.clientY,
        t: Date.now(),
      });
    },
    onPointerUp: finalizeStroke,
    onPointerLeave: finalizeStroke,
    onPointerCancel: finalizeStroke,
  };

  return { pointerHandlers, computeBiometrics };
}
